import Head from 'expo-router/head';
import { ScrollView } from 'react-native';
import { getTokenValue, Text, YStack } from 'tamagui';

import AccessPassBanner from '@/components/AccessPassBanner';

/**
 * ── /components/access-pass-banner ───────────────────────────────────────
 *
 * AccessPassBanner — the black "check out the Access Pass" card. Its copy is
 * hardwired (see the component's own docstring), so there's no prop surface
 * to enumerate; the only thing worth previewing is how it wears WIDTH, since
 * the card has no `width` prop of its own — it's `alignSelf="stretch"` plus a
 * `m={-space.xl}` negative margin that bleeds past whatever padding its
 * parent gives it, so width is entirely a function of the container it's
 * dropped into.
 *
 * Real call site: `app/events/[id]/index.tsx`, gated on `!user?.membership`
 * (logged-out or no-membership viewers only), wrapped in a bare
 * `<View py={'$space.xl'}>` inside `PageScrollView`'s standard `$lg` page
 * gutter — i.e. the ONLY real placement is full page width, which is the
 * design's 358pt case below. Grepping the whole app for `AccessPassBanner`
 * turns up no second call site and no narrower card/list context it's ever
 * squeezed into — the squeezed block below is a gallery-invented stress test
 * (same spirit as ps-button.tsx's WRAP_LABEL_WIDTH), not something a real
 * screen does, included because the text column is capped at a PERCENTAGE
 * (`TEXT_WIDTH = '85%'` in the component) rather than a fixed width, so it's
 * worth confirming the copy and the right-anchored logo mark both still hold
 * their shape once the card is narrower than its usual full-bleed width.
 * ─────────────────────────────────────────────────────────────────────────
 */

// Narrower than the real call site ever puts it — forces the 85%-width text
// column and the right-anchored logo mark (absolutely positioned, out of
// flow) to share a much tighter gap than the 358pt design case, without
// either one clipping or overlapping the other.
const SQUEEZED_WIDTH = 220;

export default function AccessPassBannerPreview() {
  return (
    <>
      <Head>
        <title>AccessPassBanner · Putting Scene</title>
      </Head>
      <ScrollView
        style={{
          flex: 1,
          backgroundColor: getTokenValue('$color.background', 'color'),
        }}
      >
        <YStack items="flex-start" gap="$lg" p="$lg">
          {/* Full page width — the design's 358pt case, and the ONLY width
            events/[id] ever actually renders this at. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              full page width — the design&apos;s 358pt case (events/[id]&apos;s
              real call site, shown when the viewer has no membership)
            </Text>
            <AccessPassBanner onPress={() => {}} />
          </YStack>

          {/* Squeezed — no real screen does this (see docstring above), but the
            85%-capped text column and the absolutely-positioned logo mark both
            need to hold their shape at a width neither was designed against. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              squeezed — gallery stress test only (no real call site is this
              narrow); confirms the text column and right-anchored art both
              still hold their shape
            </Text>
            <YStack width={SQUEEZED_WIDTH} overflow="hidden">
              <AccessPassBanner onPress={() => {}} />
            </YStack>
          </YStack>
        </YStack>
      </ScrollView>
    </>
  );
}
